import { ColumnSortReportParameter } from '@ndt/report-output/shared/models/column-sort-report-parameter';
import { TimeRangeParameterFormModel } from '@ndt/report-output/shared/models/time-range-report-parameter-value';
import { OrderingDirections } from 'apollo-angular/graphql-types';
import { ReportParameters } from 'generated/graphql-types';
import { TileSetType } from 'src/app/pages/dashboard/tile-set-type-select/models/tile-set.model';
import { AccountSelectParameterFormModel } from '../../models/account-select-parameter.value';
import { BarGraphSourceReportParameterValue } from '../../models/bar-graph-source-report-parameter-value';
import { CallDirectionsToIncludeReportParameterValue } from '../../models/call-directions-to-include-report-parameter-value';
import { CallDispositionsToIncludeReportParameterValue } from '../../models/call-dispositions-to-include-report-parameter-value';
import {
   DateRangeParameterFormModel,
   DateRangeSelectParameterFormModel,
} from '../../models/date-range-report-parameter-value';
import { DaysOfWeekReportParameterValue } from '../../models/day-of-week-report-parameter-value';
import { ExtensionGroupsReportParameterValue } from '../../models/extension-groups-report-parameter-value';
import { ExtensionsReportParameterValue } from '../../models/extensions-report-parameter-value';
import { NumberOfCallersReportParameterValue } from '../../models/number-of-callers-report-parameter-value';
import { PhoneNumbersReportParameterValue } from '../../models/phone-numbers-report-parameter-value';
import { SortByReportParameterValue } from '../../models/sort-by-report-parameter-value';
import { TimeZoneParameterFormModel } from '../../models/time-zone-parameter-value';
import {
   BooleanReportParameter,
   ReportParametersUnion,
   TileLayoutConfigurationReportParameters,
} from './report-parameter-union.model';

export interface ParameterFormValueBase {
   parameterType: ReportParameters | TileLayoutConfigurationReportParameters;
   sortOrder: number | null;
   isAskParameter?: boolean;
}

export type ParameterFormValueUnion =
   | BooleanReportParameter
   | DateRangeParameterFormModel
   | DateRangeSelectParameterFormModel
   | CallDirectionsToIncludeFormModel
   | CallDispositionsToIncludeFormModel
   | TimeZoneParameterFormModel
   | AccountSelectParameterFormModel
   | ExtensionsParameterFormModel
   | CallQueueExtensionsParameterFormModel
   | ColumnSortParameterFormModel
   | AutoAttendantExtensionsParameterFormModel
   | ExtensionGroupsParameterFormModel
   | PhoneNumbersParameterFormModel
   | DaysOfWeekParameterFormModel
   | NumberOfCallersParameterFormModel
   | SortByParameterFormModel
   | ExtensionSortByParameterFormModel
   | BarGraphSourceParameterFormModel
   | TileLayoutConfigurationNameParameterFormModel
   | TileLayoutConfigurationColorParameterFormModel
   | TileLayoutConfigurationRefreshTimeSecondsParameterFormModel
   | TileLayoutConfigurationTileSetTypeParameterFormModel
   | TimeRangeParameterFormModel;

export interface CallDirectionsToIncludeFormModel
   extends ParameterFormValueBase,
      CallDirectionsToIncludeReportParameterValue {
   parameterType: ReportParameters.CallDirectionsToInclude;
}

export interface CallDispositionsToIncludeFormModel
   extends ParameterFormValueBase,
      CallDispositionsToIncludeReportParameterValue {
   parameterType: ReportParameters.CallDispositionsToInclude;
}

export function sortParameterFormValues(first: ParameterFormValueUnion, second: ParameterFormValueUnion): number {
   return 0;
}

export interface ExtensionsParameterFormModel extends ParameterFormValueBase, ExtensionsReportParameterValue {
   parameterType: ReportParameters.ExtensionsToInclude;
}

export interface ExtensionGroupsParameterFormModel extends ParameterFormValueBase, ExtensionGroupsReportParameterValue {
   parameterType: ReportParameters.ExtensionGroupsToInclude;
}

export interface CallQueueExtensionsParameterFormModel extends ParameterFormValueBase, ExtensionsReportParameterValue {
   parameterType: ReportParameters.CallQueueExtensionsToInclude;
}

export interface ColumnSortParameterFormModel extends ParameterFormValueBase, ColumnSortReportParameter {
   parameterType: ReportParameters.ColumnSort;
}
export interface AutoAttendantExtensionsParameterFormModel
   extends ParameterFormValueBase,
      ExtensionsReportParameterValue {
   parameterType: ReportParameters.AutoAttendantExtensionsToInclude;
}

export interface PhoneNumbersParameterFormModel extends ParameterFormValueBase, PhoneNumbersReportParameterValue {
   parameterType: ReportParameters.PhoneNumbersToInclude;
}

export interface DaysOfWeekParameterFormModel extends ParameterFormValueBase, DaysOfWeekReportParameterValue {
   parameterType: ReportParameters.DaysOfTheWeek;
}

export interface NumberOfCallersParameterFormModel extends ParameterFormValueBase, NumberOfCallersReportParameterValue {
   parameterType: ReportParameters.NumberOfCallers;
}

export interface SortByParameterFormModel extends ParameterFormValueBase, SortByReportParameterValue {
   parameterType: ReportParameters.SortBy;
}

export interface ExtensionSortByParameterFormModel extends ParameterFormValueBase, SortByReportParameterValue {
   parameterType: ReportParameters.ExtensionSortBy;
}

export interface BarGraphSourceParameterFormModel extends ParameterFormValueBase, BarGraphSourceReportParameterValue {
   parameterType: ReportParameters.BarGraphSource;
}

export interface TileLayoutConfigurationNameParameterFormModel extends ParameterFormValueBase {
   parameterType: TileLayoutConfigurationReportParameters.Name;
   name: string;
}
export interface TileLayoutConfigurationColorParameterFormModel extends ParameterFormValueBase {
   parameterType: TileLayoutConfigurationReportParameters.Color;
   color: string;
}
export interface TileLayoutConfigurationRefreshTimeSecondsParameterFormModel extends ParameterFormValueBase {
   parameterType: TileLayoutConfigurationReportParameters.RefreshTimeSeconds;
   refreshTimeSeconds: number | null;
}

export interface TileLayoutConfigurationTileSetTypeParameterFormModel extends ParameterFormValueBase {
   parameterType: TileLayoutConfigurationReportParameters.TileSetType;
   tileSetType: TileSetType;
}

export function convertFromFormValueList(valueChanges: ParameterFormValueUnion[]): ReportParametersUnion[] {
   return valueChanges.map((vc) => {
      return convertFormValue(vc);
   });
}

export function convertFormValue(valueChange: ParameterFormValueUnion): ReportParametersUnion {
   switch (valueChange.parameterType) {
      case ReportParameters.CallDirectionsToInclude:
         return {
            parameterType: valueChange.parameterType,
            sortOrder: null,
            value: valueChange.value,
         };
      case ReportParameters.CallDispositionsToInclude:
         return {
            parameterType: valueChange.parameterType,
            sortOrder: null,
            value: valueChange.value,
         };
      case ReportParameters.ExtensionsToInclude:
         return {
            accountId: valueChange.accountId,
            all: valueChange.all,
            names: valueChange.extensions?.map((ext) => {
               return `${ext.number}`;
            }),
            parameterType: valueChange.parameterType,
            sortOrder: null,
            values: valueChange.extensions?.map((ext) => {
               return ext.extensionId;
            }),
         };
      case ReportParameters.ExtensionGroupsToInclude:
         return {
            accountId: valueChange.accountId,
            all: valueChange.all,
            parameterType: valueChange.parameterType,
            sortOrder: null,
            names: valueChange.extensionGroups?.map((extGroup) => {
               return `${extGroup.name}`;
            }),
            values: valueChange.extensionGroups?.map((ext) => {
               return ext.extensionGroupId;
            }),
         };
      case ReportParameters.AutoAttendantExtensionsToInclude:
         return {
            accountId: valueChange.accountId,
            all: valueChange.all,
            parameterType: valueChange.parameterType,
            sortOrder: null,
            names: valueChange.extensions?.map((ext) => {
               return `${ext.number}`;
            }),
            values: valueChange.extensions?.map((ext) => {
               return ext.extensionId;
            }),
         };
      case ReportParameters.CallQueueExtensionsToInclude:
         return {
            accountId: valueChange.accountId,
            all: valueChange.all,
            parameterType: valueChange.parameterType,
            sortOrder: null,
            names: valueChange.extensions?.map((ext) => {
               return `${ext.number}`;
            }),
            values: valueChange.extensions?.map((ext) => {
               return ext.extensionId;
            }),
         };
      case ReportParameters.ColumnSort:
         return {
            parameterType: valueChange.parameterType,
            sortOrder: null,
            sortingOrder: valueChange.sortingOrder ?? OrderingDirections.NotSet,
            value: `${valueChange.value}` || '',
         };
      case ReportParameters.ExcludeInternal:
         return {
            parameterType: valueChange.parameterType,
            sortOrder: null,
            value: !!valueChange.value,
         };
      case ReportParameters.DateRange:
         return {
            endDate: valueChange.endDate ?? null,
            parameterType: valueChange.parameterType,
            sortOrder: null,
            startDate: valueChange.startDate ?? null,
         };
      case ReportParameters.DateRangeSelect: {
         return {
            endDate: valueChange.endDate ?? null,
            field: valueChange.field ?? null,
            filterByDateRange: valueChange.filterByDateRange,
            parameterType: valueChange.parameterType,
            sortOrder: null,
            startDate: valueChange.startDate ?? null,
         };
      }
      case ReportParameters.TimeZone: {
         return {
            parameterType: valueChange.parameterType,
            sortOrder: null,
            value: valueChange.value ?? '',
         };
      }
      case ReportParameters.SelectedAccount: {
         return {
            parameterType: valueChange.parameterType,
            sortOrder: null,
            value: valueChange.value ?? 0,
            name: valueChange.name ?? '',
         };
      }
      case ReportParameters.DaysOfTheWeek: {
         return {
            parameterType: valueChange.parameterType,
            sortOrder: null,
            values: valueChange.values,
         };
      }
      case ReportParameters.PhoneNumbersToInclude:
         return {
            accountId: valueChange.accountId,
            all: valueChange.all,
            names: valueChange.phoneNumbers?.map((number) => {
               return `${number.displayName}`;
            }),
            parameterType: valueChange.parameterType,
            sortOrder: null,
            values: valueChange.phoneNumbers?.map((number) => {
               return number.phoneNumberId;
            }),
         };
      case ReportParameters.NumberOfCallers:
         return {
            parameterType: valueChange.parameterType,
            sortOrder: null,
            value: valueChange.value,
         };
      case ReportParameters.SortBy:
         return {
            parameterType: valueChange.parameterType,
            sortOrder: null,
            value: valueChange.value,
         };
      case ReportParameters.ExtensionSortBy:
         return {
            parameterType: valueChange.parameterType,
            sortOrder: null,
            value: valueChange.value,
         };
      case ReportParameters.IncludeNotMissedCalls:
         return {
            parameterType: valueChange.parameterType,
            sortOrder: null,
            value: !!valueChange.value,
         };
      case ReportParameters.SummaryOnly:
         return {
            parameterType: valueChange.parameterType,
            sortOrder: null,
            value: !!valueChange.value,
         };
      case ReportParameters.BarGraphSource:
         return {
            parameterType: valueChange.parameterType,
            sortOrder: null,
            value: valueChange.value,
         };
      case ReportParameters.GroupByExtension:
         return {
            parameterType: valueChange.parameterType,
            sortOrder: null,
            value: !!valueChange.value,
         };
      case TileLayoutConfigurationReportParameters.Name: {
         return {
            parameterType: valueChange.parameterType,
            name: valueChange.name,
            sortOrder: valueChange.sortOrder,
         };
      }
      case TileLayoutConfigurationReportParameters.Color: {
         return {
            parameterType: valueChange.parameterType,
            color: valueChange.color,
            sortOrder: valueChange.sortOrder,
         };
      }
      case TileLayoutConfigurationReportParameters.RefreshTimeSeconds: {
         return {
            parameterType: valueChange.parameterType,
            refreshTimeSeconds: valueChange.refreshTimeSeconds,
            sortOrder: valueChange.sortOrder,
         };
      }
      case TileLayoutConfigurationReportParameters.TileSetType: {
         return {
            parameterType: valueChange.parameterType,
            tileSetType: valueChange.tileSetType,
            sortOrder: valueChange.sortOrder,
         };
      }
      case ReportParameters.ShowDate: {
         return {
            parameterType: valueChange.parameterType,
            sortOrder: valueChange.sortOrder,
            value: !!valueChange.value,
         };
      }
      case ReportParameters.AggregateQueues: {
         return {
            parameterType: valueChange.parameterType,
            sortOrder: valueChange.sortOrder,
            value: !!valueChange.value,
         };
      }
      case ReportParameters.TimeRange: {
         return {
            parameterType: valueChange.parameterType,
            sortOrder: valueChange.sortOrder,
            startTime: valueChange.startTime,
            endTime: valueChange.endTime,
         };
      }
      default:
         throw new Error('Unknown parameter type on value: ' + valueChange);
   }
}
